/**
 * Release Breadcrumbs Hook
 *
 * @module features/releases/hooks/useReleaseBreadcrumbs
 * @description Breadcrumb wiring for the release timeline page: the
 * Release-QA-scoped property switcher, the in-scope navigation guard inside
 * `onPropertyChange`, and the URL-over-store reconciliation effect. The guard
 * is correctness-bearing: without it, the shared selection store's own
 * (unscoped) fallback could pick a property with no Release QA config and
 * navigate the page there.
 */

import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { usePropertyBreadcrumbs } from '../../../hooks/usePropertyBreadcrumbs';
import { useReleaseProperties } from './useReleaseProperties';

export function useReleaseBreadcrumbs() {
  const { propertyId } = useParams({ strict: false });
  const navigate = useNavigate();

  // The subnav used to list every property with a Release QA config (plus
  // untested-commit badges) — the breadcrumb switcher below is scoped to the
  // same list rather than falling back to every org property.
  const { data: releaseProperties, isLoading: releasePropertiesLoading } = useReleaseProperties();
  const releasePropertyOptions = React.useMemo(
    () => (releaseProperties ?? []).map((p) => ({ id: p.propertyId, name: p.propertyName })),
    [releaseProperties]
  );

  const result = usePropertyBreadcrumbs({
    pageBreadcrumb: {
      key: 'releases',
      label: 'Releases',
      onPress: () => navigate({ to: '/releases' }),
    },
    propertiesOverride: { items: releasePropertyOptions, isLoading: releasePropertiesLoading },
    onPropertyChange: (nextPropertyId) => {
      if (!nextPropertyId || nextPropertyId === propertyId) return;
      // Guard against the shared selection store's own (unscoped) fallback
      // picking a property that isn't in the Release QA list at all.
      if (!releasePropertyOptions.some((p) => p.id === nextPropertyId)) return;
      navigate({ to: '/releases/$propertyId', params: { propertyId: nextPropertyId } });
    },
  });

  const { propertyId: breadcrumbPropertyId, selectProperty } = result;

  // Reconcile the switcher's selection with the URL — so a direct link or
  // back/forward navigation always wins over the store's own fallback
  // resolution.
  React.useEffect(() => {
    if (propertyId && breadcrumbPropertyId !== propertyId) {
      selectProperty(propertyId);
    }
  }, [propertyId, breadcrumbPropertyId, selectProperty]);

  return result;
}
